/**
 * Экран кампаний менеджера (S5): типы, подписи и вызовы сервера.
 *
 * Все числа и состояния считает сервер (`/campaign-desk`, предпросмотр запуска);
 * здесь — только форма запроса, подписи причин и чистые помощники, которые
 * можно проверить без разметки.
 */

import { api, request, type Schemas } from '../../../../api/client';
import { defineMessages, type Message } from '../../../../i18n/define';
import { tr } from '../../../../i18n/runtime';
import { dateDay } from '../../../lib/format';

const m = defineMessages({
  fields: {
    id: 'ops.campaigns.words.fields',
    defaultMessage: '{count, plural, one {# поле} few {# поля} many {# полей} other {# поля}}',
    description: 'Число полей (контуров земли) в экране кампаний',
  },
  noDeadline: { id: 'ops.campaigns.window.noDeadline', defaultMessage: 'без срока', description: 'Подпись окна кампании или работы, у которых нет дат (менеджер); строчными' },
  approxWindow: { id: 'ops.campaigns.window.approx', defaultMessage: '≈ {range}', description: 'Окно с примерным сроком; {range} — отформатированный диапазон дат; «≈» оставить' },
  dateRange: { id: 'ops.campaigns.window.range', defaultMessage: '{from} – {to}', description: 'Окно кампании из двух дат без года; {from}/{to} — «15 мая»' },
  startsNow: { id: 'ops.campaigns.startsIn.now', defaultMessage: 'идёт', description: 'Работа плана «Скоро»: окно уже идёт; одно слово, строчными' },
  startsTomorrow: { id: 'ops.campaigns.startsIn.tomorrow', defaultMessage: 'завтра', description: 'Работа плана «Скоро»: окно откроется завтра; одно слово' },
  startsInDays: { id: 'ops.campaigns.startsIn.days', defaultMessage: 'через {days} дн.', description: 'Работа плана «Скоро»: через сколько дней откроется окно; {days} — число; «дн.» сокращённо' },
  startsApprox: { id: 'ops.campaigns.startsIn.approx', defaultMessage: '≈ {text}', description: 'Примерный срок старта; {text} — «через 12 дн.»; «≈» оставить' },
  reasonNoActor: { id: 'ops.campaigns.reason.noActor', defaultMessage: 'размещение без хозяйства', description: 'Почему поле не вошло в кампанию (менеджер): у размещения культуры не указано хозяйство' },
  reasonNoLandUse: { id: 'ops.campaigns.reason.noLandUse', defaultMessage: 'нет землепользования на дату', description: 'Почему поле не вошло в кампанию: на дату нет записи, кто пользуется землёй' },
  reasonActorMismatch: { id: 'ops.campaigns.reason.actorMismatch', defaultMessage: 'хозяйство размещения ≠ землепользователь', description: 'Почему поле не вошло в кампанию: хозяйство в размещении не совпадает с тем, кто пользуется землёй; «≠» оставить' },
  reasonFarmExcluded: { id: 'ops.campaigns.reason.farmExcluded', defaultMessage: 'исключено менеджером', description: 'Почему поле не вошло в кампанию: менеджер исключил хозяйство' },
  refusalDefault: { id: 'ops.campaigns.refusal', defaultMessage: 'Сервер не принял действие.', description: 'Ошибка в окне запуска/решения по кампании, когда сервер не объяснил причину' },
});


/** «12 полей»: слово для `countOf`. */
export const FIELDS_WORD = m.fields;

export type Desk = Schemas['CampaignDeskOut'];
export type DeskWork = Schemas['DeskWorkOut'];
export type DeskCampaign = Schemas['DeskCampaignOut'];
export type LaunchPreview = Schemas['LaunchPreviewOut'];
export type LaunchResult = Schemas['LaunchOut'];
export type Upcoming = Schemas['UpcomingOut'];
export type UpcomingItem = Schemas['UpcomingItemOut'];
export type CampaignRow = Schemas['CampaignRow'];
export type CampaignList = Schemas['CampaignList'];

/** Решение менеджера по хозяйству при запуске (№797): своё окно или исключение. */
export interface FarmRule {
  actor_id: number;
  exclude: boolean;
  reason: string;
  window_from: string;
  window_to: string;
}

export interface LaunchOptions {
  window_from: string;
  window_to: string;
  no_deadline: boolean;
  farms: FarmRule[];
}

export const EMPTY_OPTIONS: LaunchOptions = {
  window_from: '',
  window_to: '',
  no_deadline: false,
  farms: [],
};

/**
 * Тело запроса запуска: пустые даты не отправляются вовсе.
 *
 * Предпросмотр (`preview`) сервер отклоняет, если у исключения нет причины, а
 * в мастере хозяйство добавляют ДО того, как напечатана причина. Причина на
 * охват не влияет, поэтому предпросмотру уходит заглушка; в запуск — только
 * настоящая причина (без неё запуск заблокирован в самом окне).
 */
export function launchBody(options: LaunchOptions, asOf?: string, preview = false) {
  return {
    as_of: asOf || undefined,
    window_from: options.window_from || undefined,
    window_to: options.window_to || undefined,
    no_deadline: options.no_deadline,
    farms: options.farms
      .filter((rule) => rule.exclude || rule.window_from || rule.window_to)
      .map((rule) => ({
        actor_id: rule.actor_id,
        exclude: rule.exclude,
        reason: rule.exclude ? rule.reason.trim() || (preview ? '…' : '') : '',
        window_from: rule.exclude ? undefined : rule.window_from || undefined,
        window_to: rule.exclude ? undefined : rule.window_to || undefined,
      })),
  };
}

/**
 * Ключ предпросмотра: что меняет охват. Набор текста причины сюда не входит —
 * охват при этом не пересчитывается.
 */
export function previewKey(options: LaunchOptions): string {
  return JSON.stringify([
    options.window_from,
    options.window_to,
    options.no_deadline,
    options.farms
      .filter((rule) => rule.exclude || rule.window_from || rule.window_to)
      .map((rule) => [rule.actor_id, rule.exclude, rule.exclude ? '' : rule.window_from, rule.exclude ? '' : rule.window_to])
      .sort((a, b) => Number(a[0]) - Number(b[0])),
  ]);
}

/** Хозяйства из списка исключений и окон, у которых запуск ещё не может пройти. */
export function ruleIssues(options: LaunchOptions): { noReason: number[]; noWindow: number[] } {
  const noReason: number[] = [];
  const noWindow: number[] = [];
  for (const rule of options.farms) {
    if (rule.exclude) {
      if (!rule.reason.trim()) noReason.push(rule.actor_id);
    } else if (!rule.window_from || !rule.window_to) {
      noWindow.push(rule.actor_id);
    }
  }
  return { noReason, noWindow };
}

/** Правило хозяйства: найти или завести, не теряя остальных. */
export function withFarmRule(
  options: LaunchOptions,
  actorId: number,
  patch: Partial<Omit<FarmRule, 'actor_id'>>,
): LaunchOptions {
  const current = options.farms.find((rule) => rule.actor_id === actorId) ?? {
    actor_id: actorId,
    exclude: false,
    reason: '',
    window_from: '',
    window_to: '',
  };
  const next = { ...current, ...patch };
  return {
    ...options,
    farms: [...options.farms.filter((rule) => rule.actor_id !== actorId), next],
  };
}

/** Убрать хозяйство из списка исключений и своих окон. */
export function withoutFarmRule(options: LaunchOptions, actorId: number): LaunchOptions {
  return { ...options, farms: options.farms.filter((rule) => rule.actor_id !== actorId) };
}

/** «15 мая» — без года: окно плана читается внутри сезона. */
export function dayLabel(iso: string | null | undefined): string {
  if (!iso) return '…';
  const label = dateDay(iso);
  return label === '—' ? iso : label;
}

/** Окно словами; «≈» у примерного срока, «без срока», когда дат нет. */
export function windowText(
  from: string | null | undefined,
  to: string | null | undefined,
  approximate = false,
): string {
  if (!from && !to) return tr(m.noDeadline);
  const text = from === to ? dayLabel(from) : tr(m.dateRange, { from: dayLabel(from), to: dayLabel(to) });
  return approximate ? tr(m.approxWindow, { range: text }) : text;
}

/** Через сколько дней начало: «через 5 дн.», «идёт», «≈ через 12 дн.». */
export function startsIn(days: number | null | undefined, approximate: boolean): string {
  if (days === null || days === undefined) return '—';
  const base = days <= 0 ? tr(m.startsNow) : days === 1 ? tr(m.startsTomorrow) : tr(m.startsInDays, { days });
  return approximate && days > 0 ? tr(m.startsApprox, { text: base }) : base;
}

/* ------------------------------------------------- готовность и ход */

const DAY_MS = 86_400_000;
const utcDay = (iso: string) => {
  const [year = 0, month = 1, day = 1] = iso.split('-').map(Number);
  return Date.UTC(year, month - 1, day);
};

/** Дней от первой даты до второй (ISO без времени): отрицательно, если вторая раньше. */
export function daysBetween(fromIso: string, toIso: string): number {
  return Math.round((utcDay(toIso) - utcDay(fromIso)) / DAY_MS);
}

/** За сколько дней до начала окна работа попадает в «Готовы к запуску». */
export const READY_HORIZON_DAYS = 14;

export type ReadyState = 'late' | 'open' | 'soon';

/** Работа плана, по которой пора запускать кампанию. */
export interface ReadyWork {
  work: DeskWork;
  /** `late` — окно закрылось, кампании нет; `open` — окно идёт; `soon` — откроется в ближайшие дни. */
  state: ReadyState;
  /** `soon`: дней до начала; `open`: дней до конца окна (`null` — срока нет); `late`: дней после конца (положительно). */
  days: number | null;
}

const READY_ORDER: Record<ReadyState, number> = { late: 0, open: 1, soon: 2 };

/**
 * «Готовы к запуску»: работы плана без кампании, у которых окно уже закрылось,
 * идёт или откроется в ближайшие `horizon` дней. Сначала те, где время вышло,
 * затем те, чьё окно закрывается раньше. Считается на дату среза сервера.
 */
export function readyWorks(works: readonly DeskWork[], asOf: string, horizon = READY_HORIZON_DAYS): ReadyWork[] {
  const out: ReadyWork[] = [];
  for (const work of works) {
    if (!work.can_launch || work.campaign_id != null || work.status === 'cancelled') continue;
    const from = work.window_from;
    const to = work.window_to;
    let state: ReadyState = 'open';
    let days: number | null = null;
    if (to && daysBetween(asOf, to) < 0) {
      state = 'late';
      days = -daysBetween(asOf, to);
    } else if (from && daysBetween(asOf, from) > 0) {
      const until = daysBetween(asOf, from);
      if (until > horizon) continue;
      state = 'soon';
      days = until;
    } else if (to) {
      days = daysBetween(asOf, to);
    }
    out.push({ work, state, days });
  }
  const key = (row: ReadyWork) => {
    if (row.state === 'soon') return row.work.window_from ?? '9999';
    return row.work.window_to ?? '9999';
  };
  return out.sort((a, b) => READY_ORDER[a.state] - READY_ORDER[b.state] || key(a).localeCompare(key(b)) || a.work.order_no - b.work.order_no);
}

/**
 * Работы плана без кампании, окно которых откроется позже горизонта «Готовы к
 * запуску»: запускать их заранее можно, но на виду они не нужны. Ближайшие — первыми.
 */
export function laterWorks(works: readonly DeskWork[], asOf: string, horizon = READY_HORIZON_DAYS): DeskWork[] {
  return works
    .filter((work) => work.can_launch && work.campaign_id == null && work.status !== 'cancelled' && work.window_from != null && daysBetween(asOf, work.window_from) > horizon)
    .sort((a, b) => (a.window_from ?? '').localeCompare(b.window_from ?? '') || a.order_no - b.order_no);
}

/** Кампания ещё в работе: объявлена, идёт или на паузе. */
export function isOpenCampaign(status: string): boolean {
  return status === 'announced' || status === 'active' || status === 'paused';
}

/** Название кампании на экране — по работе, одно для всех строк: агроцикл уже выбран в шапке. */
export function campaignName(campaign: Pick<DeskCampaign, 'work_label'>): string {
  return campaign.work_label;
}

/** Ход кампании в гектарах работ (сервер считает, клиент только переводит микрогектары). */
export interface Progress {
  total: number;
  done: number;
  inProgress: number;
  overdue: number;
}

const toHa = (microha: number | null | undefined) => (microha ?? 0) / 1_000_000;

/** `null` — площади работ по кампании нет (ход тогда не показывается, а не рисуется нулём). */
export function progressOf(row: Pick<CampaignRow, 'area_work'> | null | undefined): Progress | null {
  const area = row?.area_work;
  if (!area || area.total_microha == null || area.total_microha <= 0) return null;
  return {
    total: toHa(area.total_microha),
    done: toHa(area.done_microha),
    inProgress: toHa(area.in_progress_microha),
    overdue: toHa(area.overdue_microha),
  };
}

export interface DeskTotals {
  /** Идут: объявлены, идут или на паузе. */
  running: number;
  paused: number;
  closed: number;
  /** Все кампании агроцикла без отменённых. */
  total: number;
  /** Гектары работ после срока по идущим кампаниям и число таких кампаний. */
  overdueHa: number;
  overdueCampaigns: number;
  /** Выполнено / всего, га работ по всем кампаниям без отменённых. */
  doneHa: number;
  totalHa: number;
  donePct: number | null;
  /** Ход известен не у всех кампаний — итог по гектарам считается по тем, у кого он есть. */
  known: number;
}

export function summarize(campaigns: readonly DeskCampaign[], progress: ReadonlyMap<number, Progress> | null): DeskTotals {
  const totals: DeskTotals = { running: 0, paused: 0, closed: 0, total: 0, overdueHa: 0, overdueCampaigns: 0, doneHa: 0, totalHa: 0, donePct: null, known: 0 };
  for (const campaign of campaigns) {
    if (campaign.status === 'cancelled') continue;
    totals.total += 1;
    const open = isOpenCampaign(campaign.status);
    if (open) totals.running += 1;
    else totals.closed += 1;
    if (campaign.status === 'paused') totals.paused += 1;
    const ha = progress?.get(campaign.id);
    if (!ha) continue;
    totals.known += 1;
    totals.doneHa += ha.done;
    totals.totalHa += ha.total;
    if (open && ha.overdue > 0) {
      totals.overdueHa += ha.overdue;
      totals.overdueCampaigns += 1;
    }
  }
  totals.donePct = totals.totalHa > 0 ? (100 * totals.doneHa) / totals.totalHa : null;
  return totals;
}

/**
 * Главное действие строки идущей кампании по контексту; остальные решения
 * уходят в меню «⋯». Приоритет: пауза → окно закрылось → есть новые поля →
 * есть выбывшие → перенос срока.
 */
export function primaryDecision(campaign: DeskCampaign, asOf: string): 'resume' | 'complete' | 'extend' | 'retire' | 'reschedule' {
  if (campaign.status === 'paused') return 'resume';
  if (campaign.window_to && daysBetween(asOf, campaign.window_to) < 0) return 'complete';
  if (campaign.outside_fields > 0) return 'extend';
  if (campaign.departed > 0) return 'retire';
  return 'reschedule';
}

/** Причина исключения поля словами (сервер присылает и текст, и код). */
const REASON_MESSAGE: Record<string, Message> = {
  no_actor: m.reasonNoActor,
  no_land_use: m.reasonNoLandUse,
  actor_mismatch: m.reasonActorMismatch,
  farm_excluded: m.reasonFarmExcluded,
};

/** Подпись причины по коду; незнакомый код — текст сервера. */
export function reasonLabel(code: string, serverText: string): string {
  const message = Object.prototype.hasOwnProperty.call(REASON_MESSAGE, code) ? REASON_MESSAGE[code] : undefined;
  return message ? tr(message) : serverText;
}

/** Отказ сервера → строка для человека (текст сервера, если он есть). */
export function refusalText(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = String((error as { message: unknown }).message);
    if (message) return message;
  }
  return tr(m.refusalDefault);
}

/* ------------------------------------------------------------ вызовы */

const post = <T>(path: string, body: object) =>
  request<T>(path, undefined, { method: 'POST', body: JSON.stringify(body) });

async function write<T>(path: string, body: object): Promise<T> {
  await api.csrf();
  return post<T>(path, body);
}

export const deskApi = {
  desk: (cycle: string | null, includeDemo: boolean) =>
    request<Desk>('/campaign-desk', { cycle: cycle ?? undefined, include_demo: includeDemo }),
  /** Ход всех кампаний района в гектарах работ (по id кампании); страницы по 200. */
  progress: async (includeDemo: boolean): Promise<Map<number, Progress>> => {
    const map = new Map<number, Progress>();
    const limit = 200;
    for (let offset = 0; offset < 2000; offset += limit) {
      const page = await request<CampaignList>('/campaigns', { include_demo: includeDemo, limit, offset });
      for (const row of page.items) {
        const progress = progressOf(row);
        if (progress) map.set(row.id, progress);
      }
      if (offset + limit >= page.page.total) break;
    }
    return map;
  },
  upcoming: (includeDemo: boolean, days = 30) =>
    request<Upcoming>('/agrocycles/upcoming', { days, include_demo: includeDemo }),
  preview: (cycle: string, workId: number, options: LaunchOptions) =>
    write<LaunchPreview>(
      `/agrocycles/${encodeURIComponent(cycle)}/works/${workId}/launch/preview`,
      launchBody(options, undefined, true),
    ),
  launch: (cycle: string, workId: number, options: LaunchOptions) =>
    write<LaunchResult>(
      `/agrocycles/${encodeURIComponent(cycle)}/works/${workId}/launch`,
      launchBody(options),
    ),
  extendPreview: (campaignId: number) =>
    write<LaunchPreview>(`/campaigns/${campaignId}/extend/preview`, {}),
  extend: (campaignId: number, reason: string) =>
    write<LaunchResult>(`/campaigns/${campaignId}/extend`, { reason }),
  retireDeparted: (campaignId: number, reason: string) =>
    write<{ count: number }>(`/campaigns/${campaignId}/retire-departed`, { reason }),
  reschedule: (campaignId: number, from: string, to: string, reason: string) =>
    write<{ moved: number; kept_with_report: number }>(`/campaigns/${campaignId}/reschedule`, {
      window_from: from || undefined,
      window_to: to || undefined,
      reason,
    }),
  decide: (campaignId: number, action: 'pause' | 'resume' | 'complete' | 'cancel', reason: string) =>
    write<{ count: number }>(`/campaigns/${campaignId}/${action}`, { reason }),
};
